import { useMemo } from 'react';

import type { useContributorsDataQuery$data } from './__generated__/useContributorsDataQuery.graphql.ts';

export type OrderType = 'count' | 'name' | 'update' | 'edits' | 'editTime';
export type IncludeType = 'authors' | 'comments' | 'labels' | 'watches';

export interface ContributorData {
	id: string;
	fullName: string;
	totalCount: number;
	edits: number;
	comments: number;
	labels: number;
	watches: number;
	lastActiveTime: number;
	lastEditTime: number;
}

export interface UseProcessedContributorsResultOptions {
	data: useContributorsDataQuery$data | undefined;
	include: IncludeType[];
	order: OrderType;
	reverse: boolean;
	showAnonymous: boolean;
	limit: number;
}

export interface UseProcessedContributorsResultReturn {
	visibleContributors: ContributorData[];
	hiddenContributors: ContributorData[];
}

const ANONYMOUS_USER_ID = 'anonymous';

const getComparator = (order: OrderType): ((a: ContributorData, b: ContributorData) => number) => {
	switch (order) {
		case 'name':
			return (a, b) => a.fullName.localeCompare(b.fullName);
		case 'update':
			return (a, b) => b.lastActiveTime - a.lastActiveTime;
		case 'edits':
			return (a, b) => b.edits - a.edits;
		case 'editTime':
			return (a, b) => b.lastEditTime - a.lastEditTime;
		case 'count':
		default:
			return (a, b) => b.totalCount - a.totalCount;
	}
};

const hasContributionInIncludedTypes = (
	contributor: ContributorData,
	include: IncludeType[],
): boolean => {
	return include.some((type) => {
		switch (type) {
			case 'authors':
				return contributor.edits > 0;
			case 'comments':
				return contributor.comments > 0;
			case 'labels':
				return contributor.labels > 0;
			case 'watches':
				return contributor.watches > 0;
			default:
				return false;
		}
	});
};

export const transformAuthorRanking = (
	ranking: NonNullable<
		NonNullable<NonNullable<useContributorsDataQuery$data['singleContent']>['contributorsMacro']>
	>['authorRankingList'],
): ContributorData[] => {
	if (!ranking) {
		return [];
	}

	const result: ContributorData[] = [];
	for (const author of ranking) {
		if (author == null) {
			continue;
		}
		const edits = author.edits ?? 0;
		const comments = author.comments ?? 0;
		const labels = author.labels ?? 0;
		const watches = author.watches ?? 0;
		const lastEditTime = author.lastEditTime ?? 0;
		const lastCommentTime = author.lastCommentTime ?? 0;
		const lastLabelTime = author.lastLabelTime ?? 0;
		const lastActiveTime = Math.max(lastEditTime, lastCommentTime, lastLabelTime);

		result.push({
			id: author.idString ?? '',
			fullName: author.fullNameString ?? '',
			totalCount: edits + comments + labels + watches,
			edits,
			comments,
			labels,
			watches,
			lastActiveTime,
			lastEditTime,
		});
	}
	return result;
};

export const useProcessedContributorsResult = ({
	data,
	include,
	order,
	reverse,
	showAnonymous,
	limit,
}: UseProcessedContributorsResultOptions): UseProcessedContributorsResultReturn => {
	return useMemo(() => {
		const contentNode = data?.singleContent;
		const authorRankingList = contentNode?.contributorsMacro?.authorRankingList;
		let contributors = transformAuthorRanking(authorRankingList ?? null);

		// Filter out anonymous user if not wanted
		if (!showAnonymous) {
			contributors = contributors.filter((c) => c.id !== ANONYMOUS_USER_ID);
		}

		// Filter by contribution types
		contributors = contributors.filter((c) => hasContributionInIncludedTypes(c, include));

		// Sort
		const comparator = getComparator(order);
		contributors.sort(reverse ? (a, b) => comparator(b, a) : comparator);

		// Split into visible and hidden
		const noLimit = limit <= -1;
		let visibleContributors: ContributorData[];
		let hiddenContributors: ContributorData[];

		if (!noLimit && limit < contributors.length) {
			visibleContributors = contributors.slice(0, limit);
			hiddenContributors = contributors.slice(limit);
		} else {
			visibleContributors = contributors;
			hiddenContributors = [];
		}

		return { visibleContributors, hiddenContributors };
	}, [data, include, order, reverse, showAnonymous, limit]);
};
